import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  CircularProgress
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";

import BasicModal from "./BasicModal";
import SelectStatus from "../SelectStatus";
import RetornoFinanceiroModal from "./RetornoFinanceiroModal";

import PlanoAcao from "../../types/negocio/PlanoAcao";
import MetaGlobal from "../../types/negocio/MetaGlobal";
import Colaborador from "../../types/negocio/Colaborador";
import { ESTRATEGIA_POR_CHAVE, type EstrategiaKey } from "../../types/constants/estrategias";
import { AMEACA_NORMALIZADO, OPORTUNIDADE_NORMALIZADO } from "../../types/constants/ClassificacaoAmbienteExterno";
import { ambienteInternoPontoFracoService } from "../../services/AmbienteInternoPontoFracoService";
import { ambienteInternoPontoForteService } from "../../services/AmbienteInternoPontoForteService";
import { ambienteExternoService } from "../../services/AmbienteExternoService";
import { planoAcaoService } from "../../services/PlanoAcaoService";
import { AmbienteInternoPontoForte } from "../../types/negocio/AmbienteInternoPontoForte";
import { AmbienteInternoPontoFraco } from "../../types/negocio/AmbienteInternoPontoFraco";
import { AmbienteExterno } from "../../types/negocio/AmbienteExterno";
import { normalize } from "../../utils/string";
import toastService from "../../utils/toastService";

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: (data: PlanoAcao) => void;
  plano: PlanoAcao;
  setPlano: React.Dispatch<React.SetStateAction<PlanoAcao>>;
  metasGlobais: MetaGlobal[];
  colaboradores: Colaborador[];
  modoEdicao?: boolean;
  planosExistentes?: any[];
};

export default function PlanoAcaoModal({
  open,
  onClose,
  onSave,
  plano,
  setPlano,
  metasGlobais,
  colaboradores,
  modoEdicao = true,
  planosExistentes = [],
}: Props) {
  const [completedSteps, setCompletedSteps] = useState<boolean[]>([
    false,
    false,
    false,
    false,
  ]);
  const [pontosFortes, setPontosFortes] = useState<AmbienteInternoPontoForte[]>(
    []
  );
  const [oportunidades, setOportunidades] = useState<AmbienteExterno[]>([]);
  const [pontosFracos, setPontosFracos] = useState<AmbienteInternoPontoFraco[]>(
    []
  );
  const [ameacas, setAmeacas] = useState<AmbienteExterno[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingLoading, setSavingLoading] = useState(false);

  // Retorno Financeiro Modal states
  const [retornoFinanceiroModalAberto, setRetornoFinanceiroModalAberto] = useState(false);
  const [statusAnterior, setStatusAnterior] = useState<string>(plano.status);

  // carregar dados adicionais para cruzamento
  useEffect(() => {
    Promise.all([
      ambienteExternoService.listarTodos(),
      ambienteInternoPontoFracoService.listarTodos(),
      ambienteInternoPontoForteService.listarTodos(),
    ])
      .then(([externos, fracos, fortes]) => {
        setAmeacas(
          externos.filter(
            (item) =>
              normalize(item.fator) === AMEACA_NORMALIZADO && item.pontuacao > 0
          )
        );
        setOportunidades(
          externos.filter(
            (item) =>
              normalize(item.fator) === OPORTUNIDADE_NORMALIZADO &&
              item.pontuacao > 0
          )
        );
        setPontosFracos(fracos.filter((item) => item.pontuacao > 0));
        setPontosFortes(fortes.filter((item) => item.pontuacao > 0));
        setLoading(false);
      })
      .catch(() => {
        toastService.error("Erro ao carregar dados de ambiente.");
        setLoading(false);
      });
  }, []);

  // controlar steps
  useEffect(() => {
    setCompletedSteps((prev) => [!!plano.fatorExternoGuid, ...prev.slice(1)]);
  }, [plano.fatorExternoGuid]);
  useEffect(() => {
    setCompletedSteps((prev) => [
      prev[0],
      !!plano.fatorInternoGuid,
      prev[2],
      prev[3],
    ]);
  }, [plano.fatorInternoGuid]);
  useEffect(() => {
    setCompletedSteps((prev) => [
      prev[0],
      prev[1],
      !!plano.metaGlobalGuid,
      prev[3],
    ]);
  }, [plano.metaGlobalGuid]);
  useEffect(() => {
    const preenchido =
      plano.acao &&
      plano.responsavelGuid &&
      plano.prazoConclusao &&
      plano.status &&
      plano.area;
    setCompletedSteps((prev) => [prev[0], prev[1], prev[2], !!preenchido]);
  }, [
    plano.acao,
    plano.responsavelGuid,
    plano.prazoConclusao,
    plano.status,
    plano.area,
  ]);

  const definirEstrategia = () => {
    const fatorExterno =
      oportunidades.find((o) => o.guid === plano.fatorExternoGuid) ||
      ameacas.find((a) => a.guid === plano.fatorExternoGuid);
    const fatorInterno =
      pontosFortes.find((pf) => pf.guid === plano.fatorInternoGuid) ||
      pontosFracos.find((pf) => pf.guid === plano.fatorInternoGuid);

    if (fatorExterno && fatorInterno) {
      const isOportunidade = oportunidades.some((o) => o.guid === fatorExterno.guid);
      const isAmeaca = ameacas.some((a) => a.guid === fatorExterno.guid);
      const isPontoForte = pontosFortes.some((pf) => pf.guid === fatorInterno.guid);
      const isPontoFraco = pontosFracos.some((pf) => pf.guid === fatorInterno.guid);

      let key: EstrategiaKey | null = null;
      if (isOportunidade && isPontoForte) {
        key = 'OFENSIVA';
      } else if (isOportunidade && isPontoFraco) {
        key = 'CONFRONTO';
      } else if (isAmeaca && isPontoForte) {
        key = 'REFORCO';
      } else if (isAmeaca && isPontoFraco) {
        key = 'DEFENSIVA';
      }

      if (!key) {
        return;
      }
      
      const estrategia = ESTRATEGIA_POR_CHAVE[key];
      plano.estrategia = estrategia.nome;
    }
  };

  const salvarPlano = async () => {
    // Verificar se o status mudou para "Finalizada"
    const mudouParaFinalizada = statusAnterior !== "Finalizada" && plano.status === "Finalizada";
    if (mudouParaFinalizada) {
      // Mostrar modal de retorno financeiro primeiro
      setRetornoFinanceiroModalAberto(true);
      return;
    }
    
    // Prosseguir com o salvamento normal
    await executarSalvamento();
  };

  const executarSalvamento = async (retornoFinanceiro?: string) => {
    setSavingLoading(true);
    definirEstrategia();

    try {
      // Verifica se o plano já existe na lista (edição) ou se é novo (criação)
      const planoJaExiste = planosExistentes.some((p) => p.guid === plano.guid);
      
      const planoComRetorno = retornoFinanceiro 
        ? { ...plano, retornoFinanceiro } 
        : plano;
      
      if (planoJaExiste) {
        await planoAcaoService.atualizar(planoComRetorno);
        toastService.success("Plano atualizado com sucesso!");
      } else {
        await planoAcaoService.criar(planoComRetorno);
        toastService.success("Plano criado com sucesso!");
      }
      
      onSave(planoComRetorno);
      onClose();
    } catch (err) {
      toastService.error("Erro ao salvar plano de ação.");
    } finally {
      setSavingLoading(false);
    }
  };

  const handleRetornoFinanceiroSave = async (retornoFinanceiro: string) => {
    setRetornoFinanceiroModalAberto(false);
    setSavingLoading(true); // Ativa o loading no BasicModal
    await executarSalvamento(retornoFinanceiro);
  };

  const handleRetornoFinanceiroCancel = () => {
    setRetornoFinanceiroModalAberto(false);
    // Reverter o status para o anterior
    setPlano(prev => ({ ...prev, status: statusAnterior }));
  };

  return (
    <>
      <BasicModal
        title={modoEdicao ? "Plano de Ação" : "Novo Plano de Ação"}
        open={open}
        onClose={onClose}
        saveButton
        onSave={salvarPlano}
        saveLoading={savingLoading}
        tela="plano-de-acao"
      >
      {loading ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ padding: 2, maxWidth: 800, margin: "auto" }}>
          {/* Campos */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Fator Externo */}
            <FormControl fullWidth color="secondary">
              <InputLabel>Fator Externo</InputLabel>
              <Select
                value={plano.fatorExternoGuid}
                onChange={(e) =>
                  setPlano((prev) => ({
                    ...prev,
                    fatorExternoGuid: e.target.value,
                  }))
                }
              >
                {[...oportunidades, ...ameacas].map((item) => (
                  <MenuItem key={item.guid} value={item.guid}>
                    {item.opcao}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Linha com 2 colunas: Fator Interno + Meta Global */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <FormControl fullWidth color="secondary">
                <InputLabel>Fator Interno</InputLabel>
                <Select
                  value={plano.fatorInternoGuid}
                  onChange={(e) =>
                    setPlano((prev) => ({
                      ...prev,
                      fatorInternoGuid: e.target.value,
                    }))
                  }
                >
                  {[...pontosFortes, ...pontosFracos].map((item) => (
                    <MenuItem key={item.guid} value={item.guid}>
                      {item.descricao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth color="secondary">
                <InputLabel>Meta Global</InputLabel>
                <Select
                  value={plano.metaGlobalGuid}
                  onChange={(e) =>
                    setPlano((prev) => ({
                      ...prev,
                      metaGlobalGuid: e.target.value,
                      area:
                        metasGlobais.find((m) => m.guid === e.target.value)?.area ||
                        "",
                    }))
                  }
                >
                  {metasGlobais.map((meta) => (
                    <MenuItem key={meta.guid} value={meta.guid}>
                      {meta.area}: {meta.meta}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Ação */}
            <TextField
              fullWidth
              label="Ação"
              variant="outlined"
              color="secondary"
              value={plano.acao}
              onChange={(e) =>
                setPlano((prev) => ({ ...prev, acao: e.target.value }))
              }
            />

            <FormControl fullWidth color="secondary">
              <InputLabel>Responsável</InputLabel>
              <Select
                value={plano.responsavelGuid}
                onChange={(e) =>
                  setPlano((prev) => ({
                    ...prev,
                    responsavelGuid: e.target.value,
                  }))
                }
              >
                {colaboradores.map((c) => (
                  <MenuItem key={c.guid} value={c.guid}>
                    {c.nomeCompleto}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", gap: 2 }}>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={ptBR}
              >
                <DatePicker
                  label="Data de Início"
                  value={plano.dataInicio ? new Date(plano.dataInicio) : null}
                  onChange={(newDate) =>
                    setPlano((prev) => ({
                      ...prev,
                      dataInicio: newDate ?? undefined,
                    }))
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      color: "secondary",
                      variant: "outlined"
                    }
                  }}
                />
              </LocalizationProvider>

              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={ptBR}
              >
                <DatePicker
                  label="Prazo de Conclusão"
                  value={plano.prazoConclusao ? new Date(plano.prazoConclusao) : null}
                  onChange={(newDate) =>
                    setPlano((prev) => ({
                      ...prev,
                      prazoConclusao: newDate ?? new Date(),
                    }))
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      color: "secondary",
                      variant: "outlined"
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>

            <SelectStatus
              value={plano.status}
              onChange={(value) => {
                setStatusAnterior(plano.status);
                setPlano((prev) => ({ ...prev, status: value }));
              }}
            />

            {/* Prioridade */}
            <FormControl fullWidth color="secondary">
              <InputLabel>Prioridade</InputLabel>
              <Select
                value={plano.prioridade || ""}
                onChange={(e) =>
                  setPlano((prev) => ({
                    ...prev,
                    prioridade: e.target.value,
                  }))
                }
              >
                <MenuItem value="Muito alta">Muito alta</MenuItem>
                <MenuItem value="Alta">Alta</MenuItem>
                <MenuItem value="Média">Média</MenuItem>
                <MenuItem value="Baixa">Baixa</MenuItem>
                <MenuItem value="Muito baixa">Muito baixa</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      )}
      </BasicModal>

      <RetornoFinanceiroModal
        open={retornoFinanceiroModalAberto}
        onClose={handleRetornoFinanceiroCancel}
        onSave={handleRetornoFinanceiroSave}
        acaoNome={plano.acao}
      />
    </>
  );
}